import { AppHeroSwitch, SwitchThumb } from '@components/HeroSwitch'
import { SectionCard } from '@components/Profile/SectionCard'
import { PushDeviceStatus } from '@components/Push/PushDeviceStatus'
import { PUSH_NOTIFICATIONS_STUDENTS } from '@constants/features'
import {
  INotificationPreference,
  NotificationCategory,
} from '@dtos/NotificationPreference'
import { useHasFeature } from '@hooks/useThemeParams'
import { useNotificationPreferencesUpdate } from '@services/api/graphql/mutations/notificationPreferencesUpdate'
import { useNotificationPreferences } from '@services/api/graphql/queries/notificationPreferences'
import { useToastController } from '@tamagui/toast'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isWeb, Spinner, Text, XStack, YStack } from 'tamagui'

export function NotificationsSection() {
  const hasPushFeature = useHasFeature(PUSH_NOTIFICATIONS_STUDENTS)

  return hasPushFeature ? <NotificationsSettings /> : null
}

function NotificationsSettings() {
  const { t } = useTranslation('Push')
  const toast = useToastController()
  const { preferences, loading, error } = useNotificationPreferences()
  const { handleUpdatePreference } = useNotificationPreferencesUpdate()
  const [savingCategory, setSavingCategory] =
    useState<NotificationCategory | null>(null)

  const showError = (message?: string) =>
    toast.show(t('preferences.errorTitle'), {
      message: message || t('preferences.errorMessage'),
      type: 'error',
    })

  async function handleToggle({
    category,
    enabled,
    editable,
  }: INotificationPreference) {
    if (!editable || savingCategory) return

    setSavingCategory(category)
    try {
      const result = await handleUpdatePreference(
        category,
        !enabled,
        isWeb ? 'members_web' : 'members_app',
      )
      if (!result || result.errors.length) showError(result?.errors.join('\n'))
    } catch {
      showError()
    } finally {
      setSavingCategory(null)
    }
  }

  function renderPreferences() {
    if (loading && !preferences) return <Spinner color="$accent" />

    if (!preferences) {
      return error ? (
        <Text
          ff="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$text-secondary"
        >
          {t('preferences.loadError')}
        </Text>
      ) : null
    }

    return preferences.map((preference) => {
      const label = t(`preferences.categories.${preference.category}.label`)

      return (
        <XStack
          key={preference.category}
          testID={`notification-preference-${preference.category}`}
          gap="$size.spacing-xs"
          alignItems="center"
          justifyContent="space-between"
        >
          <YStack flex={1} gap="$size.spacing-nano">
            <Text
              ff="$mulishBold"
              fontSize="$font-size-xxs"
              color="$text-primary"
            >
              {label}
            </Text>
            <Text
              ff="$mulishRegular"
              fontSize="$font-size-xxxs"
              color="$text-secondary"
            >
              {t(`preferences.categories.${preference.category}.description`)}
            </Text>
          </YStack>
          <AppHeroSwitch
            testID={`notification-switch-${preference.category}`}
            checked={preference.enabled}
            active={preference.enabled}
            disabled={!preference.editable || !!savingCategory}
            onPress={() => handleToggle(preference)}
            accessibilityLabel={label}
          >
            <SwitchThumb />
          </AppHeroSwitch>
        </XStack>
      )
    })
  }

  return (
    <SectionCard title={t('preferences.title')}>
      <YStack id="notifications" gap="$size.spacing-xs">
        <PushDeviceStatus />
        {renderPreferences()}
      </YStack>
    </SectionCard>
  )
}
